/**
 * Configuration Panel — appears between Phase 2 (conversation done) and
 * Phase 3 (analysis output). Lets the PM tune the 5 calculator inputs the
 * deterministic cost calculator depends on, instead of forcing them through
 * sequential chat questions.
 *
 * Values are POSTed as `inputs` on /analyze. Backend injects them into the
 * LLM prompt as a CALCULATOR INPUTS block so the math chain uses them
 * exactly.
 */
import { useState } from "react";
import { motion } from "framer-motion";

const TOKEN_TIERS_IN = [
  { key: "small", label: "Small (<500)" },
  { key: "medium", label: "Medium (500–2k)" },
  { key: "large_rag", label: "Large / RAG (2k–5k)" },
  { key: "very_large", label: "Very large (5k+)" },
];
const TOKEN_TIERS_OUT = [
  { key: "small", label: "Short (<200)" },
  { key: "medium", label: "Standard (200–800)" },
  { key: "large", label: "Long (800+)" },
];
const TASK_TYPES = [
  { key: "classification", label: "Classification" },
  { key: "extraction", label: "Extraction" },
  { key: "generation", label: "Generation" },
  { key: "multi_step", label: "Multi-step reasoning" },
  { key: "vision", label: "Vision" },
];
const QUALITY = [
  { key: "best_effort", label: "Best effort" },
  { key: "production", label: "Production quality" },
  { key: "safety_critical", label: "Safety-critical" },
];
const COST_PER_WRONG = [
  { key: "low", label: "Annoyance only ($1–10)" },
  { key: "medium", label: "Refund or rework ($10–100)" },
  { key: "high", label: "Operational impact ($100–1,000)" },
  { key: "critical", label: "Regulated / safety ($1,000+)" },
];
const REVIEW = [
  { key: "none", label: "No human review" },
  { key: "sampled", label: "Sampled QA (5%)" },
  { key: "low_conf", label: "Low-confidence only (10%)" },
  { key: "every_output", label: "Every output reviewed" },
];

const Group = ({ label, options, value, onChange, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.16em] text-[#56534A]">{label}</div>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          data-testid={`${testid}-${o.key}`}
          className={`text-xs px-3 py-1.5 border rounded-full transition-colors ${
            value === o.key
              ? "bg-[#C4521E] border-[#C4521E] text-[#F9F7F3]"
              : "border-[#DDD9CF] text-[#1A1814] hover:border-[#C4521E] hover:text-[#C4521E]"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  </div>
);

export const ConfigPanel = ({ defaults = {}, onRun, onCancel, busy }) => {
  const [tokensIn, setTokensIn] = useState(defaults.tokens_in_tier || "medium");
  const [tokensOut, setTokensOut] = useState(defaults.tokens_out_tier || "medium");
  const [task, setTask] = useState(defaults.task_type || "classification");
  const [quality, setQuality] = useState(defaults.quality || "production");
  const [costPerWrong, setCostPerWrong] = useState(defaults.cost_per_wrong_tier || "medium");
  const [review, setReview] = useState(defaults.review_tier || "sampled");

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mt-8 border border-[#DDD9CF] bg-white rounded-md p-6 xl:p-7"
      data-testid="config-panel"
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#C4521E] mb-3">
        Before we calculate
      </div>
      <h3 className="font-serif-display text-2xl text-[#1A1814]">Confirm the calculator inputs.</h3>
      <p className="mt-2 text-sm text-[#56534A] max-w-2xl leading-relaxed">
        I've pre-selected my best guesses from your context. Edit any of these and the math will
        re-run on them. Every dollar number in your output will reference exactly these inputs.
      </p>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Group label="Input tokens per call" options={TOKEN_TIERS_IN} value={tokensIn} onChange={setTokensIn} testid="cfg-tokens-in" />
        <Group label="Output tokens per call" options={TOKEN_TIERS_OUT} value={tokensOut} onChange={setTokensOut} testid="cfg-tokens-out" />
        <Group label="Task type" options={TASK_TYPES} value={task} onChange={setTask} testid="cfg-task" />
        <Group label="Quality bar" options={QUALITY} value={quality} onChange={setQuality} testid="cfg-quality" />
        <div className="md:col-span-2">
          <Group label="Cost per wrong output" options={COST_PER_WRONG} value={costPerWrong} onChange={setCostPerWrong} testid="cfg-cost-wrong" />
        </div>
        <div className="md:col-span-2">
          <Group label="Human review intensity" options={REVIEW} value={review} onChange={setReview} testid="cfg-review" />
          <p className="text-[11px] text-[#56534A] mt-2 italic">
            Reviewer cost defaults to $50/hr (domain reviewer) at 2 min/output. Shown transparently in the math.
          </p>
        </div>
      </div>

      <div className="mt-7 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={() =>
            onRun({
              tokens_in_tier: tokensIn,
              tokens_out_tier: tokensOut,
              task_type: task,
              quality,
              cost_per_wrong_tier: costPerWrong,
              review_tier: review,
            })
          }
          disabled={busy}
          className="bg-[#C4521E] text-[#F9F7F3] px-7 py-3 font-medium tracking-wide hover:bg-[#a34418] transition-colors disabled:opacity-40 flex items-center gap-2"
          data-testid="config-run"
        >
          {busy ? "Composing…" : "Run analysis"}
          <span aria-hidden="true">→</span>
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="text-sm text-[#56534A] hover:text-[#C4521E] transition-colors disabled:opacity-40"
          data-testid="config-cancel"
        >
          Cancel
        </button>
      </div>
    </motion.div>
  );
};
